import { PLAN_IDS, PLAN_NAME_MAPPER } from '@/constants';
import React, { useState } from 'react';
import UpgradeModal from "@/components/modals/UpgradeModal";
import { usePlans } from '@/contexts/PlanRulesContext';
import { ArrowUp, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { formatDate } from '@/helpers/formatters';
import { Button } from '../ui/button';
import { toast } from 'sonner';

/**
 * Plan upgrade / renew rules:
 *
 * Plan types: "regular" | "subscription"
 * Regular plans:  "Plan 99"  (id: 99161) | "plan 199" (id: 19916)
 * Subscription:   "Tier 1"   (id: 21193) | "Tier 2"   (id: 14221)
 *
 * Regular Plan 99  → upgrade to: Plan 199, Tier 1, Tier 2
 * Regular Plan 199 → upgrade to: Tier 1, Tier 2
 * Subscription Tier 1 → upgrade to: Tier 2
 * Subscription Tier 2 → no upgrade (top plan)
 *
 * Active plan  → can Renew current plan  OR  Upgrade (if upgrade exists)
 * Expired plan → can Renew current plan  AND Upgrade (if upgrade exists)
 */

/**
 * Given the current plan id, return ids of plans the user is allowed to
 * upgrade to (empty array = no upgrades available).
 */
function getAllowedUpgradeIds(currentPlanId) {
    switch (currentPlanId) {
        case PLAN_IDS.PLAN_99:
            return [PLAN_IDS.PLAN_199, PLAN_IDS.TIER_1, PLAN_IDS.TIER_2];
        case PLAN_IDS.PLAN_199:
            return [PLAN_IDS.TIER_1, PLAN_IDS.TIER_2];
        case PLAN_IDS.TIER_1:
            return [PLAN_IDS.TIER_2];
        case PLAN_IDS.TIER_2:
        default:
            return [];
    }
}

// ─── component ──────────────────────────────────────────────────────────────

function PlanInfoCard({ currentPlan }) {

    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('upgrade'); // 'upgrade' | 'renew'
    const { plans, planRules } = usePlans();

    const isExpired = !currentPlan?.active_status;
    const allPlans = plans?.plans ?? [];
    const currentId = String(currentPlan?.plan_id ?? '');


    /** Plans the user can upgrade to */
    const upgradePlans = allPlans.filter(p =>
        getAllowedUpgradeIds(currentId).includes(String(p.id))
    );

    /** Just the current plan (for renew) */
    const renewPlans = allPlans.filter(p => String(p.id) === currentId);

    const hasUpgrades = upgradePlans.length > 0;

    const openUpgradeModal = () => setModalOpen(true) || setModalMode('upgrade');
    const openRenewModal = () => setModalOpen(true) || setModalMode('renew');

    // Plans to pass into the modal depend on the action
    const modalPlans = modalMode === 'renew' ? renewPlans : upgradePlans;

    // ── render upgrade/renew action buttons ──────────────────────────────────
    const renderActions = () => {
        // Top subscription plan — no actions
        if (currentId === PLAN_IDS.TIER_2 && !isExpired) {
            return (
                <Button className="flex-1 bg-blue-600 text-white cursor-default" disabled>
                    You are on the most advanced plan
                </Button>
            );
        }

        return (
            <div className="flex gap-3 flex-wrap">
                {/* Always show Renew */}
                {
                    isExpired && (
                        <Button
                            variant="outline"
                            className="flex-1"
                            onClick={openRenewModal}
                        >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Renew Plan
                        </Button>
                    )
                }

                {/* Show Upgrade only when upgrades exist */}
                {hasUpgrades && (
                    <Button
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={openUpgradeModal}
                    >
                        <ArrowUp className="w-4 h-4 mr-2" />
                        Upgrade Plan
                    </Button>
                )}
            </div>
        );
    };

    return (
        <>
            <UpgradeModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                plans={modalPlans}
                currentPlan={planRules?.plan_info}
                modalTitle={modalMode === 'renew' ? 'Renew Your Plan' : 'Upgrade Your Plan'}
                modalSubtitle={
                    modalMode === 'renew'
                        ? 'Renew your current plan to continue enjoying uninterrupted access.'
                        : 'Unlock more features by upgrading your plan.'
                }
            />

            <Card className="border-2 border-primary/20">
                <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-muted-foreground">Current Plan</p>
                            <CardTitle className="text-xl">{currentPlan?.plan_name}</CardTitle>
                        </div>
                        <Badge
                            className={
                                currentPlan?.active_status
                                    ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100'
                                    : 'bg-red-100 text-red-700 hover:bg-red-100'
                            }
                        >
                            {currentPlan?.active_status ? 'Active' : 'Expired'}
                        </Badge>
                    </div>
                </CardHeader>

                <CardContent className="space-y-4">
                    <div className="bg-muted/50 rounded-lg p-4">
                        {isExpired ? (
                            <p className="text-2xl font-bold text-red-600">Plan Expired</p>
                        ) : (
                            <p className="text-2xl font-bold">{currentPlan?.remaining_days} Days Remaining</p>
                        )}
                        <p className="text-sm text-muted-foreground">
                            {isExpired ? 'Expired on' : 'Expires on'} {formatDate(currentPlan?.expiry_date)}
                        </p>
                    </div>

                    {renderActions()}

                    <p className="text-xs text-amber-600">
                        <strong>Note:</strong> Your plan will remain active until the expiration date even after changes.
                    </p>
                </CardContent>
            </Card>
        </>
    );
}

export default PlanInfoCard;